import { useRef, useState, useEffect } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup } from '@vibesites/runtime/react';
import { EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import { VibesitesImage, getMediaUrl, parseStorageKey, videoThumbStorageKey } from '@vibesites/ui-primitives';

const bgDef: SlotGroupDefinition = {
  label: 'Background Media',
  description: 'If a video is set, it plays as the background. Otherwise the image is used.',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'property_featured_image',
    },
    video: {
      label: 'Video',
      type: 'video',
      defaultField: 'property_featured_video',
    },
  },
};

export function GalleryHero() {
  const bg = useSlotGroup('galleryhero.bg_image', bgDef);

  const showVideo = !!bg.video;
  // Derive the poster from the *bound* video so it stays correct when the
  // operator overrides the slot to a different video. Falls back to the image.
  const thumbKey = videoThumbStorageKey(parseStorageKey(bg.video || null));
  const poster =
    (thumbKey ? getMediaUrl(thumbKey, { width: 1920 }) : null) || bg.image || '';

  // Respect prefers-reduced-motion: drop the autoplay attribute (pause() alone
  // loses the race if the video hasn't buffered yet) and pause anything already
  // playing. Leaves the poster frame showing.
  const videoRef = useRef<HTMLVideoElement>(null);
  const [reducedMotion, setReducedMotion] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const apply = () => {
      setReducedMotion(mq.matches);
      if (mq.matches) videoRef.current?.pause();
    };
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);

  return (
    <section className="relative h-[42vh] min-h-[300px] max-h-[420px] overflow-hidden">
      <EditableSlotGroup groupPath="galleryhero.bg_image" definition={bgDef} className="absolute inset-0">
        {showVideo ? (
          /* Decorative: the overlaid headline carries the content. */
          <video
            ref={videoRef}
            className="w-full h-full object-cover"
            src={bg.video || undefined}
            poster={poster}
            autoPlay={!reducedMotion}
            muted
            loop
            playsInline
            aria-hidden
          />
        ) : bg.image ? (
          <VibesitesImage url={bg.image} alt="" sizes="100vw" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-[var(--vibe-color-text)]" />
        )}
        <div className="absolute inset-0 bg-black/30" />
      </EditableSlotGroup>

      <div className="relative z-10 flex flex-col items-center justify-center h-full text-center text-white px-4" style={{ paddingTop: 'calc(var(--offer-banner-height, 0px) + 72px)' }}>
        <RichTextContent path="galleryhero.title" as="h1" className="font-heading text-3xl sm:text-4xl md:text-5xl font-normal mb-3 uppercase tracking-wider" />
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'GalleryHero',
  displayName: 'Gallery Hero',
  tokens: {
    'galleryhero.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Gallery</p>',
    },
  },
  slotGroups: {
    'galleryhero.bg_image': bgDef,
  },
};

(GalleryHero as any).contentSchema = contentSchema;

export default GalleryHero;
